// src/components/digital_twin/twin_engine/ui/OverviewTab.js
// What the asset is and where it sits: register record, TessaDEM terrain (point assets) or
// ground profile (linear assets), and — for wells — the inspection record.
import React, { memo, useMemo } from 'react';
import { Box, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { FaClipboardList, FaMountain, FaSearchLocation, FaInfoCircle } from 'react-icons/fa';
import { KV, LevelBadge, Section, Stat, TrendChart, fmtDate, useTwinTokens } from './ui';
import { registerFields } from '../assetProfile';

const INSPECTION_LEVEL = { overdue: 'critical', never_inspected: 'high', due_soon: 'watch', current: 'ok', ok: 'ok' };

function OverviewTab({ asset, profile, sources }) {
  const t = useTwinTokens();
  const fields = useMemo(() => registerFields(asset), [asset]);
  const elev = sources.elevation?.data || null;
  const terrain = elev?.terrain || null;
  const prof = elev?.profile || null;
  const well = sources.wellIntel?.data || null;
  const ins = well?.inspection || null;
  const profilePoints = useMemo(() => (prof?.points || []).map((p) => ({ ts: p[3], value: p[2] })), [prof]);

  return (
    <VStack align="stretch" spacing={4}>
      <Section title={`About this ${profile.label.toLowerCase()}`} icon={FaInfoCircle}>
        <Text fontSize="13px" color={t.muted}>{profile.about}</Text>
      </Section>

      <Section title="Asset register" icon={FaClipboardList}>
        {fields.length ? fields.map((f) => <KV key={f.label} label={f.label} value={f.value} />) : <Text fontSize="13px" color={t.muted}>No register fields recorded.</Text>}
        {asset.latitude != null && <KV label="Coordinates" value={`${Number(asset.latitude).toFixed(6)}, ${Number(asset.longitude).toFixed(6)}`} />}
        {asset.elevation != null && <KV label={profile.wellIntel ? 'Recorded depth / elevation (register)' : 'Surveyed elevation'} value={`${asset.elevation} m`} />}
      </Section>

      <Section title="Terrain (TessaDEM)" icon={FaMountain}
        right={elev?.source ? <Text fontSize="11px" color={t.faint}>{elev.source}{elev.fetched_at ? ` · ${fmtDate(elev.fetched_at)}` : ''}</Text> : null}>
        {sources.elevation?.status === 'error' ? (
          <Text fontSize="13px" color="orange.400">Elevation service unavailable: {sources.elevation.error}</Text>
        ) : !elev || elev.ground_elevation_m == null && !prof ? (
          <Text fontSize="13px" color={t.muted}>No ground elevation stored for this asset yet. Run the elevation backfill (Admin → Elevation) to fetch it.</Text>
        ) : (
          <VStack align="stretch" spacing={3}>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
              <Stat label="Ground" value={elev.ground_elevation_m != null ? `${Number(elev.ground_elevation_m).toFixed(2)} m` : '—'} sub="above sea level" />
              {terrain && <Stat label="Depression" value={terrain.depression_m != null ? `${terrain.depression_m} m` : '—'} sub={terrain.depression_m > 0.25 ? 'terrain low' : 'not a low'} color={terrain.depression_m > 0.25 ? 'cyan.400' : undefined} hint="How far the asset sits below the mean of the ground around it" />}
              {terrain && <Stat label="Inflow" value={terrain.inflow_fraction != null ? `${Math.round(terrain.inflow_fraction * 100)}%` : '—'} sub="of directions drain here" />}
              {terrain && <Stat label="Local slope" value={terrain.local_slope_pct != null ? `${terrain.local_slope_pct}%` : '—'} sub={`within ${terrain.radius_m ?? '—'} m`} />}
              {prof && <Stat label="Grade" value={prof.grade_pct != null ? `${prof.grade_pct}%` : '—'} sub={`${Math.round(prof.length_m || 0)} m long`} />}
              {prof && <Stat label="Range" value={`${prof.min_m}–${prof.max_m} m`} sub={`${prof.low_points?.length || 0} low point(s)`} />}
            </SimpleGrid>
            {profilePoints.length > 1 && (
              <Box>
                <Text fontSize="11px" fontWeight="700" color={t.muted} mb={1}>GROUND PROFILE ALONG THE LINE (m by chainage)</Text>
                <TrendChart points={profilePoints} height={110} unit=" m" label="Ground elevation along the asset" format={(v) => v.toFixed(2)} xFormat={(v) => `${Math.round(v)} m`} xTip={(v) => `chainage ${Math.round(v)} m`} />
              </Box>
            )}
          </VStack>
        )}
      </Section>

      {profile.wellIntel && (
        <Section title="Inspection record" icon={FaSearchLocation}
          right={ins?.status ? <LevelBadge level={INSPECTION_LEVEL[ins.status] || 'info'}>{ins.status.replace(/_/g, ' ')}</LevelBadge> : null}>
          {!ins ? (
            <Text fontSize="13px" color={t.muted}>{sources.wellIntel?.status === 'error' ? `Well intelligence unavailable: ${sources.wellIntel.error}` : 'No inspection data from well intelligence.'}</Text>
          ) : (
            <>
              <KV label="Last inspected" value={ins.last_inspected_date ? `${fmtDate(ins.last_inspected_date)}${ins.last_inspected_by ? ` by ${ins.last_inspected_by}` : ''}` : 'never'} />
              <KV label="Next due" value={ins.next_due_date ? fmtDate(ins.next_due_date) : '—'} color={ins.overdue_days > 0 ? 'red.400' : undefined} />
              {ins.overdue_days > 0 && <KV label="Overdue by" value={`${ins.overdue_days} days`} color="red.400" />}
              <KV label="Interval" value={ins.interval_days ? `${ins.interval_days} days` : '—'} />
              {ins.clearance_score != null && <KV label="Clearance" value={`${ins.clearance_score}%${ins.clearance_trend ? ` (${ins.clearance_trend})` : ''}`} color={ins.clearance_score < 50 ? 'red.400' : ins.clearance_score < 70 ? 'orange.400' : undefined} />}
              {ins.last_cleaned_date && <KV label="Last cleaned" value={fmtDate(ins.last_cleaned_date)} />}
              {ins.cleaning_overdue_days > 0 && <KV label="Cleaning overdue" value={`${ins.cleaning_overdue_days} days`} color="orange.400" />}
              {ins.inspection_count != null && <KV label="Inspections on record" value={ins.inspection_count} />}
            </>
          )}
        </Section>
      )}
    </VStack>
  );
}

export default memo(OverviewTab);
